import React, { useState, useEffect } from 'react';
import { getAvatarColor } from '@sahaj/utils';

export interface WorkspaceLogoProps {
  workspaceId?: string | undefined;
  name?: string | undefined;
  avatarUrl?: string | null | undefined;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fallbackToDefaultLogo?: boolean;
  className?: string;
  alt?: string;
}

const SIZE_CLASSES = {
  xs: 'w-6 h-6 text-[10px] rounded-md',
  sm: 'w-8 h-8 text-xs rounded-lg',
  md: 'w-10 h-10 text-sm rounded-xl',
  lg: 'w-12 h-12 text-lg rounded-2xl',
  xl: 'w-16 h-16 text-2xl rounded-2xl',
};

export const WorkspaceLogo: React.FC<WorkspaceLogoProps> = ({
  workspaceId,
  name = 'Sahaj Connect',
  avatarUrl,
  size = 'md',
  fallbackToDefaultLogo = false,
  className = '',
  alt,
}) => {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [avatarUrl]);

  const initial = name ? name.trim().charAt(0).toUpperCase() : 'S';
  const bgColor = getAvatarColor(name || workspaceId || 'workspace');
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const roundedClass = sizeClass.split(' ').filter(c => c.startsWith('rounded-')).join(' ');

  const hasValidImage = Boolean(avatarUrl && !imgError && avatarUrl.trim().length > 0);

  if (hasValidImage) {
    return (
      <div className={`relative inline-flex items-center justify-center shrink-0 overflow-hidden ${sizeClass} ${className}`}>
        <img
          src={avatarUrl!}
          alt={alt || name || 'Workspace Logo'}
          onError={() => setImgError(true)}
          className={`w-full h-full object-cover ${roundedClass}`}
          loading="lazy"
        />
      </div>
    );
  }

  if (fallbackToDefaultLogo) {
    return (
      <div className={`relative inline-flex items-center justify-center shrink-0 ${sizeClass} ${className}`}>
        <img
          src="/logo.png"
          alt="Sahaj Logo"
          onError={() => setImgError(true)}
          className={`w-full h-full object-contain ${roundedClass}`}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative inline-flex items-center justify-center font-bold text-white shadow-2xs select-none shrink-0 ${sizeClass} ${roundedClass} ${className}`}
      style={{ backgroundColor: bgColor }}
      title={name}
    >
      {initial}
    </div>
  );
};
